import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Injector,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from '../../data/models';
import { MenuState } from './menu-state';
import { CartStore } from './cart-store';
import { DEFAULT_TEXT_SIZE } from '../../core/constants';
import { TabId } from './menu.types';
import { NotFound } from './not-found/not-found';
import { ImageViewer } from './overlays/image-viewer/image-viewer';
import { InfoSheet } from './overlays/info-sheet/info-sheet';
import { CartSheet } from './overlays/cart-sheet/cart-sheet';
import { ItemSheet } from './overlays/item-sheet/item-sheet';
import { RestaurantHeader } from './components/restaurant-header';
import { BannerCarousel } from './components/banner-carousel';
import { CategoryTabs } from './components/category-tabs';
import { MenuSearch } from './components/menu-search';
import { LayoutToggle } from './components/layout-toggle';
import { MenuSkeleton } from './components/menu-skeleton';
import { MenuItemCard } from './components/menu-item-card';
import { ActionBar } from './components/action-bar';
import { OrdersSheet } from './overlays/orders-sheet/orders-sheet';
import { WaiterSheet } from './overlays/waiter-sheet/waiter-sheet';
import { ToastHost } from '../../shared/toast/toast-host';
import { TableStore } from './table-store';
import { TableSocketService } from '../../core/table-socket.service';
import { ThemeLoader } from '../../core/theme-loader.service';
import { PromoStore } from './promo-store';
import { BannerStore } from './banner-store';

@Component({
  selector: 'app-qr-menu',
  imports: [
    NotFound,
    ImageViewer,
    InfoSheet,
    CartSheet,
    ItemSheet,
    RestaurantHeader,
    BannerCarousel,
    CategoryTabs,
    MenuSearch,
    LayoutToggle,
    MenuSkeleton,
    MenuItemCard,
    ActionBar,
    OrdersSheet,
    WaiterSheet,
    ToastHost,
    TranslatePipe,
  ],
  providers: [MenuState, CartStore, TableStore, TableSocketService, PromoStore, BannerStore],
  host: {
    '[style.--brand-primary]': 'menu.brandColor()',
    '[style.--brand-ink]': 'menu.palette().ink',
    '[style.--brand-fill]': 'menu.palette().fill',
    '[style.--brand-on-fill]': 'menu.palette().onFill',
    '[style.--brand-secondary]': 'menu.palette().brandDark',
    // null for the default template → no attribute at all, so it leaves the DOM untouched.
    '[attr.data-template]': 'templateAttr()',
  },
  templateUrl: './qr-menu.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './qr-menu.css',
})
export class QrMenu {
  protected menu = inject(MenuState);
  protected cart = inject(CartStore);
  protected table = inject(TableStore);
  private promo = inject(PromoStore);
  private socket = inject(TableSocketService);
  private doc = inject(DOCUMENT);
  private themeLoader = inject(ThemeLoader);
  private injector = inject(Injector);

  /** data-template value for the host: null while default (leaves the DOM clean), else the key. */
  protected readonly templateAttr = computed(() => {
    const t = this.themeLoader.applied();
    return t === 'default' ? null : t;
  });

  /** The chosen template's CSS is in effect (applied), or default, or it failed — i.e. not mid-load. */
  private readonly themeReady = computed(() => {
    const want = this.menu.designTemplate();
    return this.themeLoader.applied() === want || this.themeLoader.failed() === want;
  });

  /**
   * Keep the skeleton up while the menu data loads AND while the chosen theme's CSS is still
   * loading, so the guest never sees the default look flash before the theme applies. The default
   * template needs no file, so it reveals immediately once data is in.
   */
  protected readonly showSkeleton = computed(
    () => this.menu.isLoading() || (this.menu.loaded() !== null && !this.themeReady()),
  );

  protected gridClass = computed(() =>
    this.menu.layout() === 'list'
      ? 'qm-grid grid grid-cols-1 gap-8'
      : 'qm-grid grid grid-cols-2 gap-4',
  );

  // String refs + type-only generics keep the overlays out of the initial
  // bundle: class references here would defeat @defer chunking.
  private viewer = viewChild<ImageViewer>('viewerRef');
  private infoSheet = viewChild<InfoSheet>('infoSheetRef');
  private cartSheet = viewChild<CartSheet>('cartSheetRef');
  private ordersSheet = viewChild<OrdersSheet>('ordersSheetRef');
  private waiterSheet = viewChild<WaiterSheet>('waiterSheetRef');
  private itemSheet = viewChild<ItemSheet>('itemSheetRef');
  private menuError = viewChild<ElementRef<HTMLElement>>('menuErrorRef');

  /** The info icon appears only once the deferred sheet chunk is loaded. */
  infoSheetReady = computed(() => this.infoSheet() !== undefined);
  /** Card details buttons appear only once the deferred item sheet chunk is loaded. */
  protected readonly itemSheetReady = computed(() => this.itemSheet() !== undefined);
  /** The «Счёт» button appears only once its deferred sheet chunk is loaded. */
  protected readonly billReady = computed(() => this.ordersSheet() !== undefined);
  /** Same for «Позвать официанта»: with several orders its tap opens the deferred picker. */
  protected readonly waiterReady = computed(
    () => this.menu.showCallWaiter() && this.waiterSheet() !== undefined,
  );

  /** Add controls show when ordering is on, a table was scanned (hasOrders implied that before;
   * the create branch must say it — a table-less link or the admin preview has nowhere to send
   * a cart), no bill has been confirmed, and the cart has somewhere to go: an active order to
   * append to, or the venue lets the guest open one. */
  protected readonly canAddToCart = computed(
    () =>
      this.menu.ordering() &&
      this.menu.hasTable() &&
      !this.table.billConfirmed() &&
      (this.table.hasOrders() || this.menu.orderCreate()),
  );

  protected readonly dockVisible = computed(
    () => this.waiterReady() || (this.menu.showBill() && this.billReady()),
  );
  protected readonly cartVisible = computed(
    () => this.canAddToCart() && !this.cart.isEmpty() && this.cartSheet() !== undefined,
  );
  protected readonly showBar = computed(() => this.dockVisible() || this.cartVisible());

  /** Previous canAddToCart — only the flip needs the focus rescue below. */
  private couldAddToCart = false;

  constructor() {
    // Load the active design template's CSS at runtime (nothing for `default`) and let the loader
    // flip the host [data-template] only once the file is in — theme switch stays atomic.
    effect(() => this.themeLoader.apply(this.menu.designTemplate()));
    // Scale the whole menu from the admin-set base font size. Applied as a percentage on
    // <html> (rem is relative to the root), not absolute px, so it composes with the user's
    // own browser font-size preference instead of overriding it (WCAG SC 1.4.4).
    effect(() => {
      const scale = (this.menu.baseFontSize() / DEFAULT_TEXT_SIZE) * 100;
      this.doc.documentElement.style.fontSize = `${scale}%`;
    });
    effect(() => {
      const loaded = this.menu.loaded();
      if (loaded) this.cart.reconcile(loaded.items);
    });
    // Closing the gate (a confirmed bill, or the last order gone) unmounts every add control at
    // once — the «+» that had focus goes with them and drops it to <body>. Same landing the cart
    // sheet uses when its trigger disappears.
    effect(() => {
      const can = this.canAddToCart();
      const lost = this.couldAddToCart && !can;
      this.couldAddToCart = can;
      if (!lost) return;
      afterNextRender(
        () => {
          if (this.doc.activeElement && this.doc.activeElement !== this.doc.body) return;
          const main = this.doc.querySelector('main');
          if (main instanceof HTMLElement) {
            main.tabIndex = -1;
            main.focus({ preventScroll: true });
          }
        },
        { injector: this.injector },
      );
    });
    // Retrying a failed menu load unmounts the «Повторить» button (loading branch), dropping
    // focus to <body>; whenever the error view (re)mounts, land keyboard focus on it.
    effect(() => {
      this.menuError()?.nativeElement.focus();
    });
    // Table-service loads lazily: only after the menu is on screen and only when a table was
    // scanned. Fires the orders fetch + socket connect as a second wave — never blocks the
    // first paint, never a burst at bootstrap.
    effect(() => {
      const table = this.table.tableUuid();
      if (!this.menu.hasMenu() || !table) return;
      // Table name is always fetched when a table was scanned, so the guest sees which table.
      this.table.loadTable();
      if (this.menu.showCallWaiter() || this.menu.showBill() || this.menu.ordering()) {
        void this.socket.connect(table);
      }
      if (this.menu.showBill() || this.menu.ordering()) {
        this.table.loadOrders();
      }
    });
    // Акции не критичны: только после позиций меню, со столом или без; hasMenu не дёргается от смены языка.
    effect(() => {
      if (this.menu.hasMenu()) this.promo.load();
    });
    // Баннеры не критичны: список — сразу, параллельно с меню; картинку стор качает только после меню.
    inject(BannerStore).load();
    // Reset the root-level styles our effects write, so a future routed teardown can't leave a
    // stale font-scale on <html>. (No-op today: this page lives for the session.)
    inject(DestroyRef).onDestroy(() => {
      this.doc.documentElement.style.fontSize = '';
    });
  }

  onSelectCategory(id: TabId): void {
    this.menu.selectCategory(id);
    const win = this.doc.defaultView;
    const reduce = win?.matchMedia('(prefers-reduced-motion: reduce)').matches;
    win?.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
  }

  openImage(thumb: HTMLImageElement): void {
    this.viewer()?.open(thumb);
  }

  openInfo(trigger: HTMLElement): void {
    this.infoSheet()?.open(trigger);
  }

  openCart(trigger: HTMLElement): void {
    this.cartSheet()?.open(trigger);
  }

  onCallWaiter(trigger: HTMLElement): void {
    // The orders check is a round trip, and the dock's other buttons stay live meanwhile: a guest
    // who has opened another sheet by then must not get the picker stacked on it — picking would
    // pop history and close that sheet too. Nothing was sent; the button is free to tap again.
    this.table.callWaiter(() => {
      if (!this.doc.querySelector('dialog[open]')) this.waiterSheet()?.open(trigger);
    });
  }

  openBill(trigger: HTMLElement): void {
    this.ordersSheet()?.open(trigger);
  }

  openItemDetails(item: MenuItem, trigger: HTMLElement): void {
    this.itemSheet()?.openItem(item, trigger);
  }
}
